import { message } from 'antd'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import * as userIdentityApi from '@/api/userIdentity'

export const useUserEmailAliases = (userId: string | null) =>
  useQuery({
    queryKey: ['user-email-aliases', userId],
    queryFn: () => userIdentityApi.fetchUserEmailAliases(userId!),
    enabled: !!userId,
  })

export const useCreateUserEmailAlias = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ userId, data }: { userId: string; data: userIdentityApi.UserEmailAliasCreatePayload }) =>
      userIdentityApi.createUserEmailAlias(userId, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['user-email-aliases', variables.userId] })
      message.success('Login alias добавлен')
    },
    onError: (error: any) => {
      message.error(error?.response?.data?.detail || 'Ошибка добавления login alias')
    },
  })
}

export const useUpdateUserEmailAlias = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({
      userId,
      aliasId,
      data,
    }: {
      userId: string
      aliasId: string
      data: userIdentityApi.UserEmailAliasUpdatePayload
    }) => userIdentityApi.updateUserEmailAlias(userId, aliasId, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['user-email-aliases', variables.userId] })
      message.success('Login alias обновлен')
    },
    onError: (error: any) => {
      message.error(error?.response?.data?.detail || 'Ошибка обновления login alias')
    },
  })
}

export const useDeleteUserEmailAlias = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ userId, aliasId }: { userId: string; aliasId: string }) =>
      userIdentityApi.deleteUserEmailAlias(userId, aliasId),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['user-email-aliases', variables.userId] })
      message.success('Login alias удален')
    },
    onError: (error: any) => {
      message.error(error?.response?.data?.detail || 'Ошибка удаления login alias')
    },
  })
}

export const useUserAuthIdentities = (userId: string | null) =>
  useQuery({
    queryKey: ['user-auth-identities', userId],
    queryFn: () => userIdentityApi.fetchUserAuthIdentities(userId!),
    enabled: !!userId,
  })

export const useUserLoginReadiness = (userId: string | null) =>
  useQuery({
    queryKey: ['user-login-readiness', userId],
    queryFn: () => userIdentityApi.fetchUserLoginReadiness(userId!),
    enabled: !!userId,
  })

export const useCreateUserAuthIdentity = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ userId, data }: { userId: string; data: userIdentityApi.UserAuthIdentityCreatePayload }) =>
      userIdentityApi.createUserAuthIdentity(userId, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['user-auth-identities', variables.userId] })
      message.success('Auth identity добавлена')
    },
    onError: (error: any) => {
      message.error(error?.response?.data?.detail || 'Ошибка добавления auth identity')
    },
  })
}

export const useUpdateUserAuthIdentity = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({
      userId,
      identityId,
      data,
    }: {
      userId: string
      identityId: string
      data: userIdentityApi.UserAuthIdentityUpdatePayload
    }) => userIdentityApi.updateUserAuthIdentity(userId, identityId, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['user-auth-identities', variables.userId] })
      message.success('Auth identity обновлена')
    },
    onError: (error: any) => {
      message.error(error?.response?.data?.detail || 'Ошибка обновления auth identity')
    },
  })
}

export const useDeleteUserAuthIdentity = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ userId, identityId }: { userId: string; identityId: string }) =>
      userIdentityApi.deleteUserAuthIdentity(userId, identityId),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['user-auth-identities', variables.userId] })
      message.success('Auth identity удалена')
    },
    onError: (error: any) => {
      message.error(error?.response?.data?.detail || 'Ошибка удаления auth identity')
    },
  })
}

export const usePreviewUserYandexLink = () =>
  useMutation({
    mutationFn: ({ userId, data }: { userId: string; data: userIdentityApi.UserYandexLinkPreviewPayload }) =>
      userIdentityApi.previewUserYandexLink(userId, data),
  })

export const useApplyUserYandexLink = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ userId, data }: { userId: string; data: userIdentityApi.UserYandexLinkPreviewPayload }) =>
      userIdentityApi.applyUserYandexLink(userId, data),
    onSuccess: async (_, variables) => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['user-login-readiness', variables.userId] }),
        queryClient.invalidateQueries({ queryKey: ['users'] }),
      ])
      message.success('Live Yandex link создан')
    },
    onError: (error: any) => {
      message.error(error?.response?.data?.detail || 'Ошибка создания live Yandex link')
    },
  })
}

export const useUnlinkUserYandexLink = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ userId, data }: { userId: string; data: userIdentityApi.UserYandexLinkUnlinkPayload }) =>
      userIdentityApi.unlinkUserYandexLink(userId, data),
    onSuccess: async (_, variables) => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['user-login-readiness', variables.userId] }),
        queryClient.invalidateQueries({ queryKey: ['users'] }),
      ])
      message.success('Live Yandex link удален')
    },
    onError: (error: any) => {
      message.error(error?.response?.data?.detail || 'Ошибка удаления live Yandex link')
    },
  })
}
